import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';

export default function InvoiceSubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [canClose, setCanClose] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    console.log('[InvoiceSubscriptionSuccess] Page loaded with session:', sessionId);

    // Check if opened from web app (has window.opener)
    if (window.opener && !window.opener.closed) {
      try {
        console.log('[InvoiceSubscriptionSuccess] Sending success message to parent window');

        // Send message to parent
        window.opener.postMessage(
          { type: 'STRIPE_CHECKOUT_SUCCESS', sessionId },
          window.location.origin
        );

        // Try multiple times to close the tab (browser sometimes needs delay)
        console.log('[InvoiceSubscriptionSuccess] Attempting to close tab...');

        // Try immediately
        window.close();

        // Try again after 100ms
        setTimeout(() => window.close(), 100);

        // Try again after 300ms
        setTimeout(() => window.close(), 300);

        // If we're still here after 500ms, window.close() likely failed
        setTimeout(() => {
          if (!window.closed) {
            setCanClose(false);
            console.log('[InvoiceSubscriptionSuccess] Auto-close failed, showing manual actions');

            // Also try to navigate parent back to invoices page
            if (window.opener && !window.opener.closed) {
              try {
                window.opener.location.href = `${window.location.origin}/invoices`;
              } catch (e) {
                console.log('[InvoiceSubscriptionSuccess] Could not navigate parent:', e);
              }
            }
          }
        }, 500);
      } catch (error) {
        console.error('[InvoiceSubscriptionSuccess] Failed to send message to parent:', error);
        setCanClose(false);
      }
    }
    // Fallback: Direct navigation (no parent window)
    else {
      console.log('[InvoiceSubscriptionSuccess] No parent window - user navigated directly');
      setCanClose(false);

      // Redirect to invoices page after 2 seconds
      setTimeout(() => {
        console.log('[InvoiceSubscriptionSuccess] Redirecting to invoices page');
        navigate('/invoices');
      }, 2000);
    }
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6 animate-scale-in">
        {/* Success Icon */}
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
              {canClose ? (
                <Loader2 className="w-14 h-14 text-white animate-spin" />
              ) : (
                <CheckCircle className="w-14 h-14 text-white" strokeWidth={2.5} />
              )}
            </div>
          </div>
        </div>

        {/* Title */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {canClose ? 'Processing...' : 'Payment Successful!'}
          </h1>
          <p className="text-gray-600">
            {canClose
              ? 'Activating your invoice subscription...'
              : 'Your invoice subscription is now active!'
            }
          </p>
        </div>

        {/* Manual actions if auto-close failed */}
        {!canClose && (
          <div className="pt-4 space-y-3">
            {window.opener ? (
              <>
                <p className="text-sm text-gray-500">
                  This window should have closed automatically
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      if (window.opener && !window.opener.closed) {
                        window.opener.focus();
                      }
                      window.close();
                    }}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  >
                    Return to Invoices
                  </Button>
                  <Button
                    onClick={() => window.close()}
                    variant="outline"
                    className="flex-1"
                  >
                    Close This Tab
                  </Button>
                </div>
              </>
            ) : (
              <Button
                onClick={() => navigate('/invoices')}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                Go to Invoices
              </Button>
            )}
          </div>
        )}

        {/* Redirect message if no parent window */}
        {!canClose && !window.opener && (
          <p className="text-sm text-gray-500">
            Redirecting to invoices page...
          </p>
        )}
      </div>
    </div>
  );
}
